/**
 * Filter Apply Component
 *
 * Mobile-only button that applies filters and closes the filter modal.
 *
 * @component filter-apply
 * @extends HTMLElement
 *
 * @description
 * This component is only used in the mobile filter modal. When clicked, it:
 * 1. Closes the dialog
 * 2. Triggers AJAX fetch with selected filters
 *
 * Without this button, users would have to wait for each checkbox click to trigger
 * an AJAX request. This allows them to select multiple filters, then apply once.
 *
 * @behavior
 * - Automatically sets role="button" and tabindex="0" for accessibility
 * - Responds to click, Enter key, and Space key
 * - Calls collection-filter.applyFiltersAndClose() method
 *
 * @mobile_only
 * Desktop dropdowns apply filters immediately on checkbox change (no apply button needed).
 * Mobile uses "silent mode" - checkboxes don't trigger AJAX until this button is clicked.
 *
 * @accessibility
 * - role="button" - Announces as button to screen readers
 * - tabindex="0" - Keyboard focusable
 * - Enter/Space keys - Standard button keyboard interaction
 *
 * @example
 * ```liquid
 * <dialog-component id="filter-modal">
 *   <div class="modal-content">
 *     <!-- Filter checkboxes -->
 *     <filter-count param="*"></filter-count>
 *     <filter-clear>Clear All</filter-clear>
 *
 *     <filter-apply>Apply Filters</filter-apply>
 *   </div>
 * </dialog-component>
 * ```
 *
 * @dependencies
 * - collection-filter.js - Provides applyFiltersAndClose() method
 * - dialog.js - Modal dialog that gets closed
 */
class FilterApply extends HTMLElement {
  #filter = null;

  connectedCallback() {
    // Set ARIA attributes for accessibility
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');

    // Find parent collection-filter element
    this.#filter = this.closest('collection-filter');

    if (!this.#filter) {
      console.warn('filter-apply: No collection-filter parent found');
      return;
    }

    // Add event listeners
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
  }

  handleClick = (e) => {
    e.preventDefault();
    this.applyFilters();
  };

  handleKeydown = (e) => {
    // Handle Enter and Space keys for keyboard accessibility
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.applyFilters();
    }
  };

  applyFilters() {
    if (this.#filter?.applyFiltersAndClose) {
      this.#filter.applyFiltersAndClose();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }
}

if (!customElements.get('filter-apply')) {
  customElements.define('filter-apply', FilterApply);
}
